



































































































































import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";
import { Validator } from "vee-validate";
import Button from "@/shared/components/ui/Button.vue";
import NoAccountMessage from "@/employee/components/NoAccountMessage.vue";
import { formatCurrency } from "@/shared/currency";
import moment from "moment";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import {
  FundingOption,
  PersonalContributionRequest
} from "@/shared/types";
import { useUserStore } from "@/shared/store/user";
import { useHsaLimitStore } from "@/shared/store/hsalimit";
import { usePartnerPortalStore } from "@/employee/store/partnerPortal";
import { useHsaStore } from "@/shared/store/hsa";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { useContributionStore } from "@/shared/store/contribution";
import { useBankAccountStore } from "@/employee/store/bankAccount";
import { useHsaTotalStore } from "@/shared/store/hsaTotal";

const OptionPersonal = "personal";
const PersonalAccountPrefix = "linkedAccountId:";

@Component({
  components: {
    Button,
    NoAccountMessage,
    GenericLoadingSpinner
  }
})
export default class PersonalOneTimeContributionForm extends Vue {
  $validator: Validator;

  hsaTotalStore = useHsaTotalStore();
  bankAccountStore = useBankAccountStore();
  contributionStore = useContributionStore();
  custodianSettingsStore = useCustodianSettingsStore();
  hsaStore = useHsaStore();
  partnerPortalStore = usePartnerPortalStore();
  hsaLimitStore = useHsaLimitStore();
  userStore = useUserStore();

  proposedContribution = 0;
  isFormEditMode = false;
  contributionType = "";
  OptionPersonal = OptionPersonal;
  formatCurrency = formatCurrency;
  isPriorYear = false;
  priorYear = moment().year() - 1;
  isSubmitting = false;
  currentYearStartDate = moment(`01/01/${moment().year()}`);

  get currentYearTaxCutoffDate () {
    // adjust tax cutoff date for 2021; see BEND-7096
    const currentYear = moment().year();
    return currentYear === 2021 ? moment(`05/17/${currentYear}`) : moment(`04/15/${currentYear}`);
  }

  mounted (): void {
    this.hsaLimitStore.getHsaAccountLimits();
    this.bankAccountStore.getBankAccountStatus();
    if (!this.hsaTotalStore.isLoaded) {
      this.hsaTotalStore.getUserPayrollSummary();
    }
    this.hsaStore.loadTransactionTotals({ year: moment().year() });
    this.hsaStore.loadTransactionTotals({ year: this.priorYear });
    this.contributionStore.getPriorYearContributionEligibilityStatus();
    this.bankAccountStore.getPriorYearContributionCutoffDate();
    this.bankAccountStore.loadLinkedBankAccounts();
    this.partnerPortalStore.getPartnerPortalStatus();
    this.initializeData();
    this.custodianSettingsStore.getCustodianSettings();
  }

  get portalFunctionalityDisabled (): boolean {
    return this.custodianSettingsStore.portalFunctionalityDisabled;
  }

  onSubmit (): void {
    if (this.portalFunctionalityDisabled) {
      return;
    }

    if (this.isSubmitting) {
      return;
    }
    this.$validator.validateAll()
        .then(isValid => {
          if (!isValid) {
            return;
          }
          this.isSubmitting = true;

          if (this.selectedLinkedAccount) {
            this.submitAch(this.proposedContribution);
          }
        });
  }

  submitAch (amount): void {
    if (this.portalFunctionalityDisabled) {
      return;
    }

    const request: PersonalContributionRequest = {
      amount,
      isPriorYear: this.isPriorYear,
      linkedAccountId: parseInt(this.selectedLinkedAccount)
    };
    this.contributionStore.processPersonalContribution(request).then(() => {
      this.proposedContribution = 0;
      this.isSubmitting = false;
      this.$validator.reset();
    });
  }

  get buttonTitle (): string {
    return this.isSubmitting ? "Processing..." : "Submit Contribution";
  }

  contributionTypeSelectChange (): void {
    this.proposedContribution = 0;
  }

  contributionYearSelectChange (): void {
    this.$validator.validateAll();
  }

  get showNoAccountMessage (): boolean {
    return !this.partnerPortalStore.hasPortalAccess && !this.showLoading;
  }

  get showContributionYearSelect (): boolean {
    return this.canShowPriorYearContributionOption && !!this.selectedLinkedAccount;
  }

  get canShowPriorYearContributionOption (): boolean {
    return (this.contributionStore.allowPriorYearContribution
        && this.priorYearHsaLimit
        && this.priorYearHsaLimit > 0
        && this.bankAccountStore.linkedAccountExists) && this.custodianSettingsStore.priorYearContributionOption;
  }

  get priorYearHsaLimit (): number {
    const priorYear = moment().year() - 1;
    return this.hsaLimitStore.getHsaLimitByYear(priorYear);
  }

  get displayCurrencyInput (): boolean {
    return !this.displayMaximumAmountReachedWarning && !!this.selectedLinkedAccount;
  }

  get contributionYearSelectOptions (): { text: string, value: boolean }[] {
    const currentYear = moment().year();
    const previousYear = moment().subtract(1, "y").year();

    return [
      {
        text: `${currentYear}`,
        value: false
      },
      {
        text: `${previousYear}`,
        value: true
      } ];
  }

  get selectedYear (): number {
    return this.isPriorYear ? this.priorYear : moment().year();
  }

  get showLoading (): boolean {
    return !this.isLoaded;
  }

  get disableSubmit (): boolean {
    if (this.portalFunctionalityDisabled) {
      return true;
    }

    if (this.bankAccountStore.isBankAccountClosed) {
      return true;
    }

    return this.$validator.errors.any() ||
        this.isSubmitting ||
        this.displayMaximumAmountReachedWarning;
  }

  get showContributionForm (): boolean {
    return this.isLoaded && this.bankAccountStore.verifiedLinkedAccounts.length > 0;
  }

  get showLinkAccountMessage (): boolean {
    return this.isLoaded && this.bankAccountStore.verifiedLinkedAccounts.length === 0;
  }

  get showContributionTypeOptions () {
    return this.contributionTypeOptions.length > 1;
  }

  get defaultLinkedAccountName (): string {
    return this.contributionTypeOptions.length === 1 && this.contributionTypeOptions[0].text;
  }

  get contributionTypeOptions (): { text: string, value: string }[] {
    if (!this.isLoaded) {
      return [];
    }

    return this.bankAccountStore.sortedLinkedContributionAccounts("defaultContributionAccountId").map(account => ({
      text: account.bankAccountName + " " + account.bankAccountNumber,
      value: `${PersonalAccountPrefix}${account.linkedBankAccountId}`
    }));
  }

  get formattedPriorYearCutoffDate (): string {
    return moment(this.bankAccountStore.priorYearContributionsCutoffDate).format("MMM D, YYYY");
  }

  get formattedPriorYearTaxCutoffDate (): string {
    return moment(this.currentYearTaxCutoffDate).format("MMM D, YYYY");
  }

  get isBeforePriorYearCutoffDate (): boolean {
    const currentDate = moment();
    return (currentDate <= moment(this.bankAccountStore.priorYearContributionsCutoffDate)) &&
        (currentDate >= this.currentYearStartDate);
  }

  get isAfterPriorYearCutoffDate (): boolean {
    const currentDate = moment();
    return (currentDate > moment(this.bankAccountStore.priorYearContributionsCutoffDate)) &&
        (currentDate <= this.currentYearTaxCutoffDate);
  }

  get displayPriorYearPreCutoffCopy (): boolean {
    return this.isPriorYear && this.isBeforePriorYearCutoffDate;
  }

  get displayPriorYearPostCutoffCopy (): boolean {
    return !this.canShowPriorYearContributionOption && this.isAfterPriorYearCutoffDate;
  }

  get selectedLinkedAccount (): string {
    const contributionTypePrefix = this.contributionType.substr(0, PersonalAccountPrefix.length);
    return contributionTypePrefix === PersonalAccountPrefix
        && this.contributionType.substr(PersonalAccountPrefix.length);
  }

  get isLoaded (): boolean {
    return this.hsaTotalStore.isLoaded && this.bankAccountStore.linkedAccountsLoaded;
  }

  @Watch("contributionTypeOptions")
  initializeData (): void {
    this.contributionType = this.contributionTypeOptions.length ? this.contributionTypeOptions[0].value : "";
  }

  get validateProposedContribution () {
    return {
      required: true,
      decimal: 2,
      min_value_usd: 0.01,
      max_value_usd: this.maximumRemainingPersonalContribution
    };
  }

  get displayMaximumAmountReachedWarning (): boolean {
    return this.selectedLinkedAccount && this.maximumRemainingPersonalContribution < 0.01 && this.selectedYear ===
        this.priorYear;
  }

  get maximumRemainingPersonalContribution (): number {
    if (this.isPriorYear) {
      return this.maximumPreviousYearContribution;
    }
    return this.hsaTotalStore.maximumRemainingContribution;
  }

  get maximumPreviousYearContribution (): number {
    if (this.userStore.employeeFundingOption === FundingOption.BendCalculatePull) {
      return Math.max(this.priorYearHsaLimit - this.hsaTotalStore.priorYearPayrollContributionTotal
          - this.hsaStore.totalEmployeePersonalContributions(this.priorYear) -
          this.hsaLimitStore.otherHsaAmount(this.priorYear), 0);
    } else {
      return Math.max(this.priorYearHsaLimit - this.hsaStore.bankDirectContributions(this.priorYear)
          - this.hsaStore.totalEmployerPayrollContributions(this.priorYear)
          - this.hsaStore.employeePostTaxContributions(this.priorYear)
          - this.hsaStore.employeePreTaxContributions(this.priorYear)
          - this.hsaStore.totalEmployeePersonalContributions(this.priorYear) -
          this.hsaLimitStore.otherHsaAmount(this.priorYear), 0);
    }
  }

}
